import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Modal from "components/modal/modal";
import FormInput from "components/input/form-input";
import FormSelect from "components/input/form-select";
import FormTextarea from "components/input/form-textarea";
import FormCheckbox from "components/input/form-checkbox";
import FormSuccess from "components/modal/form-success";
import { FORMSPREE_KEYS, formspreeUrl } from "config/formspree";
import { useModal } from "context/modal-context";
import { programmes } from "data/programmes";

const teamSizeOptions = ["1 - 5", "6 - 15", "16 - 30", "31 - 50", "50+"];
const deliveryFormatOptions = ["Virtual", "On-site", "Hybrid"];

const schema = yup.object({
  orgName: yup.string().required("Organization name is required"),
  contact: yup.string().required("Contact person is required"),
  jobTitle: yup.string().required("Job title is required"),
  email: yup.string().email("Enter a valid email").required("Work email is required"),
  phone: yup.string().required("Phone number is required"),
  teamSize: yup.string(),
  deliveryFormat: yup.string(),
  additional: yup.string(),
});

type FormValues = yup.InferType<typeof schema>;

interface Props {
  open: boolean;
  handleClose: () => void;
}

const CorporateTrainingModal = ({ open, handleClose }: Props) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedProgrammes, setSelectedProgrammes] = useState<string[]>([]);
  const { modalPayload } = useModal();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema as any),
  });

  useEffect(() => {
    if (open && modalPayload?.programme) {
      setSelectedProgrammes([modalPayload.programme]);
    }
  }, [open, modalPayload]);

  const toggleProgramme = (title: string) =>
    setSelectedProgrammes((prev) =>
      prev.includes(title) ? prev.filter((a) => a !== title) : [...prev, title]
    );

  const onSubmit = async (data: FormValues) => {
    setLoading(true);
    setSubmitError(null);
    try {
      const res = await fetch(formspreeUrl(FORMSPREE_KEYS.corporateTraining), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          organizationName: data.orgName,
          contactPerson: data.contact,
          jobTitle: data.jobTitle,
          workEmail: data.email,
          phone: data.phone,
          areaOfInterest: selectedProgrammes.join(", "),
          teamSize: data.teamSize,
          preferredDeliveryFormat: data.deliveryFormat,
          additionalInformation: data.additional,
        }),
      });
      if (!res.ok) throw new Error("Submission failed");
      setSubmitted(true);
      reset();
      setSelectedProgrammes([]);
    } catch {
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onClose = () => {
    reset();
    setSelectedProgrammes([]);
    setSubmitted(false);
    setSubmitError(null);
    handleClose();
  };

  return (
    <Modal open={open} handleClose={onClose} className="w-[560px] lg:p-8 p-6">
      {submitted ? (
        <FormSuccess
          title="Request Sent!"
          message="Thank you! We've received your training request and will be in touch shortly."
          onClose={onClose}
        />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col w-full gap-6">
          <div className="flex flex-col gap-1 pr-8">
            <h2 className="text-[28px] font-bold text-[#1A1A2E] leading-tight">
              Request for Corporate Training
            </h2>
            <p className="text-sm text-[#6B6B6B]">
              Tell us about your need and how we can provide the best solution for you
            </p>
          </div>

          <section className="max-h-[75vh] w-full flex flex-col gap-4 overflow-y-auto scrollbar-none">
            <FormInput
              label="Organization Name"
              placeholder="Abc Co-operative"
              register={register("orgName")}
              error={errors.orgName}
            />
            <FormInput
              label="Contact Person"
              placeholder="Abudu Nation"
              register={register("contact")}
              error={errors.contact}
            />
            <FormInput
              label="Job Title"
              placeholder="Abudu Nation"
              register={register("jobTitle")}
              error={errors.jobTitle}
            />
            <FormInput
              label="Work Email"
              type="email"
              placeholder="abc@gmail.co"
              register={register("email")}
              error={errors.email}
            />
            <FormInput
              label="Phone number"
              placeholder="90908449840"
              register={register("phone")}
              error={errors.phone}
            />
            <div className="flex flex-col gap-2">
              <p className="text-sm text-dark-100">
                Area of Interest <span className="text-[#9E9E9E]">(Select all that apply)</span>
              </p>
              {programmes.map((p) => (
                <FormCheckbox
                  key={p.slug}
                  label={p.title}
                  checked={selectedProgrammes.includes(p.title)}
                  onChange={() => toggleProgramme(p.title)}
                />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Controller
                name="teamSize"
                control={control}
                render={({ field, fieldState }) => (
                  <FormSelect
                    label="Team Size"
                    placeholder="Select an option"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    options={teamSizeOptions}
                    error={fieldState.error}
                  />
                )}
              />
              <Controller
                name="deliveryFormat"
                control={control}
                render={({ field, fieldState }) => (
                  <FormSelect
                    label="Preferred Delivery format"
                    placeholder="Select an option"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    options={deliveryFormatOptions}
                    error={fieldState.error}
                  />
                )}
              />
            </div>
            <FormTextarea
              label="Additional Information"
              placeholder="Write your message"
              register={register("additional")}
              error={errors.additional}
            />
          </section>

          {submitError && (
            <p className="text-sm text-red-500">{submitError}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="h-12 px-8 rounded-full bg-[#CBA135] text-[#1A1A2E] font-semibold text-sm transition-opacity hover:opacity-90 disabled:opacity-60 w-fit"
          >
            {loading ? "Sending..." : "Send Request"}
          </button>
        </form>
      )}
    </Modal>
  );
};

export default CorporateTrainingModal;
